// ADR 7.10.26w §2 — the companion host's children: four fixed slots.
//
// Always the same four, each `null`-capable, so no slot ever renumbers another
// and a fold that opens or closes changes classes and remounts nothing:
//
//   0  the switcher — only beside two pages, and only while both surfaces are open
//   1  the tutor panel → lazy `TutorDock`
//   2  the card panel  → lazy `AppCardHost`
//   3  the rest panel  → `CompanionRest` — only beside two pages
//
// One page, the panels are `display: contents` and the dock's column or sheet
// lays out as it always has. Beside two pages the front panel flexes and the
// others are Tailwind's `hidden` class — never the `hidden` attribute, whose UA
// `display: none` loses to a `contents` class — so a surface behind keeps its
// draft and its view. `contents` is also inline, because `useDockSize` walks
// past it by computed `display` and jsdom applies no Tailwind.
//
// Imports no wasm: every surface comes through `gated()` (25.9.26f).

import { lazy, Suspense, useId, type CSSProperties, type ReactNode } from 'react'

import { gated } from '../../app/wasmGate'
import { PAGED } from '../../layout/spread/readSpread'
import { useSpreadValue } from '../../layout/windowClassContext'
import { useAppCardState } from '../apps/sheet/AppCardProvider'
import type { Shown } from './companionFront'
import { CompanionSwitcher, type SwitcherIds } from './CompanionSwitcher'
import { useCompanion } from './CompanionProviders'
import type { SurfaceHost } from './hosts'

const TutorDock = lazy(async () => ({
  default: await gated(() => import('../tutor/dock/TutorDock'), 'TutorDock'),
}))
const AppCardHost = lazy(async () => ({
  default: await gated(() => import('../apps/sheet/AppCardHost'), 'AppCardHost'),
}))
const CompanionRest = lazy(async () => ({
  default: await gated(() => import('./CompanionRest'), 'CompanionRest'),
}))

type PanelState = 'front' | 'behind' | 'loose'

/** `min-h-0 flex-1`, not `h-full`: the host has a `max-h` and no definite height, so the surface scrolls, not the host. */
const PANEL: Record<PanelState, string> = {
  front: 'flex min-h-0 flex-1 flex-col',
  behind: 'hidden',
  loose: 'contents',
}

const LOOSE: CSSProperties = { display: 'contents' }

/** The dock cell, lazily, in the slot it has always had (moved here from `ShellBody`). */
function DockCell({ host }: { host: SurfaceHost | null }) {
  if (host === null) return null
  return (
    <Suspense fallback={null}>
      <TutorDock host={host} />
    </Suspense>
  )
}

function Panel({ state, id, labelledBy, testId, children }: {
  state: PanelState
  id: string
  labelledBy: string | undefined
  testId: string
  children: ReactNode
}) {
  const paged = state !== 'loose'
  return (
    <div
      id={id}
      data-testid={testId}
      role={paged ? 'tabpanel' : undefined}
      aria-labelledby={paged ? labelledBy : undefined}
      className={PANEL[state]}
      style={paged ? undefined : LOOSE}
    >
      {children}
    </div>
  )
}

function useIds(): SwitcherIds {
  const base = useId()
  return {
    tutor: { tab: `${base}-tab-tutor`, panel: `${base}-panel-tutor` },
    app_card: { tab: `${base}-tab-card`, panel: `${base}-panel-card` },
  }
}

export function CompanionContents() {
  const paged = PAGED[useSpreadValue().layout]
  const companion = useCompanion()
  const { open } = useAppCardState()
  const ids = useIds()
  const both = companion.dockDrawn !== null && open !== null
  const state = (surface: Shown): PanelState => (!paged ? 'loose' : companion.shown === surface ? 'front' : 'behind')
  const labelled = (tab: string) => (both ? tab : undefined)
  return (
    <>
      {paged && both ? <CompanionSwitcher ids={ids} /> : null}
      <Panel state={state('tutor')} id={ids.tutor.panel} labelledBy={labelled(ids.tutor.tab)} testId="companion-tutor">
        <DockCell host={companion.dockDrawn} />
      </Panel>
      <Panel state={state('app_card')} id={ids.app_card.panel} labelledBy={labelled(ids.app_card.tab)} testId="companion-card">
        {open === null ? null : <Suspense fallback={null}><AppCardHost /></Suspense>}
      </Panel>
      {paged ? (
        <Panel state={state('rest')} id={`${ids.tutor.panel}-rest`} labelledBy={undefined} testId="companion-rest">
          <Suspense fallback={null}><CompanionRest /></Suspense>
        </Panel>
      ) : null}
    </>
  )
}
